import { useLazyQuery, useMutation } from '@apollo/client';
import CustomizeLayoutQuiz from 'component/layout_quiz/layout_quiz';
import { useAuth } from 'contexts/AuthContext';
import { useEffect, useState, useRef, useCallback } from 'react';
import _valuesIn from 'lodash/valuesIn';
import _isEqual from 'lodash/isEqual';
import _isEmpty from 'lodash/isEmpty';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { CREATE_ANSWER } from '../../../graph_ql/getQuiz/mutation';
import { GET_QUESTION_DATA, GET_QUIZ_RESPONSE } from '../../../graph_ql/getQuiz/queries';
import { PATH } from '../../../routes/constant';
import { useStyles } from './styles';
import { useGetGender } from '../../../hooks/useGetGender';
import { EGENDER, quizContextType } from 'types/quiz';
import { getFashionRestrictionQuizId, getFashionRestrictionSubject } from 'utils/utils';
import { Spinner } from 'react-bootstrap';
import { setFashionRestrictionDefault } from 'redux/authentication/user_detail';
import { getAnsweredQuestions } from 'utils/convertListString';
import { TextErr } from 'component/customize_style_component/styled_title';
import { TextField } from '@material-ui/core';
import useValidateAnswers from '../hooks/useValidateAnswers';

interface IQuestionData {
    value?: string;
    id?: string;
    title?: string;
    isSelect?: boolean;
}

interface Ianswer {
    title?: string;
    value?: string;
    id?: String;
    answerId?: string;
    userProperty?: string;
    answerUpdated?: string;
    action?: string;
    isSkipped?: string;
    required?: boolean;
    options?: IQuestionData[];
    questionDataSubjectTags?: string[];
    questionDataSubject?: string;
}

interface Imutation {
    questionId: String;
    questionSubject: String;
    contextType: String;
    contextId: String;
    value: String;
    valueType: String;
    id?: String;
    questionDataIds: any[];
}

interface IProps {
    handleNext?: (value: Boolean, order?: number, gender?: EGENDER) => void;
}

const nothingSpecific = 'nothingSpecific';

const FashionRestriction = (props: IProps) => {
    const { handleNext } = props;
    const { userName } = useParams<{ userName: string }>();
    const { currentUser } = useAuth();
    const gender = useGetGender(Boolean(userName));
    const [fashionRestrictionQuizList, setFashionRestrictionList] = useState<Ianswer[]>([]);
    const location = useLocation();
    const dispatch = useDispatch();
    const classes = useStyles();
    const currentSubjectTags = useRef<string[] | null>(null);
    const [questionsLoading, setQuestionsLoading] = useState(false);
    const [answerLoading, setAnswerLoading] = useState(false);
    const [customValue, setCustomvalue] = useState('');
    const [customValueError, setCustomValueError] = useState(false);
    const [quizData, setQuizData] = useState<any>(null);
    const isUpdate = location.pathname.includes(PATH.MY_STYLE_PREFERENCES);
    const history = useHistory();
    const [answerAlreadyUpdated, setAnswerUpdated] = useState(false);
    const isValidAnswer = useValidateAnswers(
        isUpdate,
        fashionRestrictionQuizList?.filter((item: any) => item?.options?.filter((item) => item.isSelect).length > 0)
            .length > 0,
        answerAlreadyUpdated,
    );

    const [getQuestionData, { loading: loadingQuestionData }] = useLazyQuery(GET_QUESTION_DATA, {
        onCompleted(data) {
            const list = fashionRestrictionQuizList.map((item) => {
                if (_isEqual(item.questionDataSubjectTags, currentSubjectTags.current)) {
                    return {
                        ...item,
                        options:
                            data?.getQuestionData?.data?.map((questionData) => {
                                if (item?.value) {
                                    let parsedValue = JSON.parse(item?.value || '');
                                    const answeredFashionRestriction = parsedValue?.find(
                                        (i) => questionData.id === i.id,
                                    );
                                    if (
                                        answeredFashionRestriction &&
                                        answeredFashionRestriction?.id === questionData?.id
                                    ) {
                                        if (answeredFashionRestriction.customValue) {
                                            setCustomvalue(answeredFashionRestriction.customValue);
                                        }
                                        return { ...questionData, isSelect: true };
                                    } else {
                                        return questionData;
                                    }
                                } else {
                                    return questionData;
                                }
                            }) || [],
                    };
                }
                return { ...item };
            });
            setFashionRestrictionList(list);
        },
    });

    useEffect(() => {
        if (fashionRestrictionQuizList.length) {
            let firstFashionRestrictionQuiz = fashionRestrictionQuizList[0];
            if (!firstFashionRestrictionQuiz.options?.length) {
                currentSubjectTags.current = firstFashionRestrictionQuiz?.questionDataSubjectTags || null;
                getQuestionData({
                    variables: {
                        input: {
                            subject: getFashionRestrictionSubject(gender),
                            tags: firstFashionRestrictionQuiz?.questionDataSubjectTags,
                            title: '',
                            paging: { page: 0, size: 50 },
                        },
                    },
                });
            }
        }
    }, [fashionRestrictionQuizList, getQuestionData, gender]);

    const [getQuizResponse] = useLazyQuery(GET_QUIZ_RESPONSE, {
        onCompleted(data) {
            const listAnswers = _valuesIn(data.getQuizResponse?.answers);
            const listQuestions = _valuesIn(data.getQuizResponse?.questions);
            setQuizData(data.getQuizResponse);
            dispatch(
                setFashionRestrictionDefault(
                    getAnsweredQuestions(data.getQuizResponse?.answers, data.getQuizResponse?.questions),
                ),
            );
            const list = listQuestions.map((item) => {
                const answeredFashionRestriction = listAnswers.find((i) => i.questionId === item.id);
                let options: IQuestionData[] = [];
                if (fashionRestrictionQuizList.length) {
                    let existingFashionRestrictions = fashionRestrictionQuizList.find((i) => i.id === item.id);
                    if (!_isEmpty(existingFashionRestrictions) && existingFashionRestrictions?.options) {
                        options = existingFashionRestrictions?.options;
                    }
                }
                if (answeredFashionRestriction) {
                    return {
                        ...item,
                        value: answeredFashionRestriction.value,
                        options: options,
                        answerId: answeredFashionRestriction.id,
                    };
                }
                return { ...item, options: options };
            });
            setFashionRestrictionList(list);
            setQuestionsLoading(false);
        },
    });

    const handleRedirection = useCallback(() => {
        if (location.pathname === PATH.QUIZ_MANAGER) {
            if (handleNext) {
                handleNext(false, 9, gender === EGENDER.MALE ? EGENDER.MALE : EGENDER.FEMALE);
            }
            history.push(`${PATH.QUIZ_MANAGER}/?signup=${Date.now()}`);
        }
    }, [gender, handleNext, location, history]);

    const [answerMutation] = useMutation(CREATE_ANSWER, {
        async onCompleted(response) {
            await getQuizResponse({
                variables: {
                    input: {
                        quizId: getFashionRestrictionQuizId(gender),
                        contextType: quizContextType,
                        contextId: getFashionRestrictionQuizId(gender),
                        userId: currentUser.uid,
                    },
                },
            });
            setAnswerLoading(false);
            handleRedirection();
        },
        onError(error) {
            console.log('Create Failed: ', error);
        },
    });

    const createAnswer = (input: Imutation) => {
        const variables = {
            input,
        };
        answerMutation({ variables });
    };

    useEffect(() => {
        if (currentUser && Boolean(!userName) && gender) {
            setQuestionsLoading(true);
            getQuizResponse({
                variables: {
                    input: {
                        quizId: getFashionRestrictionQuizId(gender),
                        contextType: quizContextType,
                        contextId: getFashionRestrictionQuizId(gender),
                        userId: currentUser.uid,
                    },
                },
            });
        }
    }, [currentUser, userName, gender, getQuizResponse, handleNext]);

    const renderFashionRestrictionQuiz = (slide, index) => {
        return (
            <div className={`${classes.styleContainer} style-container`} key={index}>
                <div className={classes.styleContainer}>
                    {/* <div className={classes.heading}>
                        <div>{slide.title}</div>
                        {slide.subTitle}
                    </div> */}
                    {slide?.options?.length && !loadingQuestionData ? (
                        <div className={classes.optionContainer}>
                            {slide?.options?.map((option, index) => (
                                <div
                                    key={index}
                                    className={classes.option}
                                    style={{
                                        background: option.isSelect ? '#F2D4BD' : '#ffffff',
                                        border: option.isSelect && '1px solid #ffffff',
                                    }}
                                    onClick={() => handleMultiSelect(slide, option)}
                                >
                                    {option.title}
                                </div>
                            ))}
                            {slide?.options?.filter((item) => item.value === 'other' && item.isSelect)?.length ? (
                                <div className={`mt-3 w-100 ${classes.customTextField}`}>
                                    <TextField
                                        style={{
                                            width: '100%',
                                            fontSize: 16,
                                        }}
                                        value={customValue}
                                        multiline
                                        maxRows={4}
                                        required
                                        onChange={handleTextFieldChange}
                                    />
                                    {customValueError && (
                                        <TextErr style={{ display: 'block' }}>This field is required</TextErr>
                                    )}
                                </div>
                            ) : null}
                        </div>
                    ) : loadingQuestionData ? (
                        <div className={`${classes.optionContainer} w-100`}>
                            <Spinner animation="border" style={{ marginLeft: 10, width: 18, height: 18 }} />
                        </div>
                    ) : null}
                </div>
            </div>
        );
    };
    const handleMultiSelect = (slide, option) => {
        setAnswerUpdated(true);
        let updateFashionRestrictionQuizList = fashionRestrictionQuizList.map((item) => {
            if (item.id === slide.id) {
                return {
                    ...item,
                    options: item?.options?.map((optionItem) => {
                        if (option.value === nothingSpecific) {
                            if (option.id === optionItem.id) {
                                return { ...optionItem, isSelect: !optionItem.isSelect };
                            } else {
                                return { ...optionItem, isSelect: false };
                            }
                        } else if (option.id === optionItem.id) {
                            return { ...optionItem, isSelect: !option.isSelect };
                        } else if (optionItem.value === nothingSpecific) {
                            return { ...optionItem, isSelect: false };
                        }
                        return optionItem;
                    }),
                };
            } else {
                return item;
            }
        });
        setFashionRestrictionList(updateFashionRestrictionQuizList);
    };

    const handleTextFieldChange = (e) => {
        setCustomvalue(e.target.value);
        if (customValueError) {
            setCustomValueError(false);
        }
    };

    const handleSaveAnswer = () => {
        if (fashionRestrictionQuizList.length) {
            let quiz = fashionRestrictionQuizList[0];
            let selectedOptionList: Ianswer[] = [];
            let isCustomValueMissing = false;
            quiz?.options?.forEach((item) => {
                if (item?.isSelect && item.value === 'other') {
                    if (customValue) {
                        selectedOptionList = [...selectedOptionList, item];
                    } else {
                        isCustomValueMissing = true;
                        setCustomValueError(true);
                    }
                } else if (item?.isSelect && item.value !== 'other') {
                    selectedOptionList = [...selectedOptionList, item];
                }
            });
            if (!isCustomValueMissing) {
                const selectedOptionValue = selectedOptionList?.map((item) => {
                    let selectedItem = {
                        id: item.id,
                        title: item.title,
                        value: item.value,
                    };
                    if (item.value === 'other') {
                        return { ...selectedItem, type: 'custom', customValue };
                    } else {
                        return { ...selectedItem, type: 'default', customValue: '' };
                    }
                });
                const selectedOptionId = selectedOptionList?.map((item) => {
                    return item.id;
                });
                setAnswerLoading(true);
                createAnswer({
                    contextId: getFashionRestrictionQuizId(gender),
                    contextType: quizContextType,
                    questionId: quiz?.id || '',
                    questionSubject: quiz.questionDataSubject || '',
                    value: JSON.stringify(selectedOptionValue),
                    valueType: 'STRING',
                    id: quiz?.answerId,
                    questionDataIds: selectedOptionId || [],
                });
            }
        }
    };

    return (
        <CustomizeLayoutQuiz
            quizId={getFashionRestrictionQuizId(gender)}
            title={Boolean(!userName) ? quizData?.subTitle : ''}
            titlePage={quizData?.title}
            handleAnswer={() => {
                handleSaveAnswer();
            }}
            isUpdate={location.pathname !== PATH.QUIZ_MANAGER}
            isDisplayContent={Boolean(!userName)}
            hideBottomNav={location.pathname === PATH.QUIZ_MANAGER}
            requiredQuestion={true}
            loading={answerLoading}
            containerLoading={questionsLoading}
            nextBtnDisabled={!isValidAnswer}
        >
            <div className={classes.container}>
                {fashionRestrictionQuizList.map((slide, index) => {
                    return renderFashionRestrictionQuiz(slide, index);
                })}
            </div>
        </CustomizeLayoutQuiz>
    );
};
export default FashionRestriction;
